import { useState } from 'react';
import { CoachResponse, WorkoutRecommendation } from '../types';
import {
  CoachContext,
  askCoach,
  buildCoachContext,
  coachResponseToRecommendation,
} from '../services/aiCoach';
import { generateWorkoutRecommendation } from '../services/recommendations';
import { CheckInFeeling, DailyCheckIn } from '../services/checkIn';
import {
  DEMO_RACES,
  DEMO_RECOVERY,
  DEMO_SLEEP,
  DEMO_STATS,
  DEMO_USER,
  DEMO_WORKOUTS,
} from '../services/demo';
import { Dashboard } from './Dashboard';
import { localDate } from '../utils/date';

interface DemoDayProps {
  onExit: () => void;
  onSignUp: () => void;
}

/**
 * Zero-signup sample day: the real Dashboard driven by a fictional athlete,
 * entirely in memory. The check-in, plan generation, and coach chat are the
 * live product paths (the AI coach is called for real); nothing is persisted.
 */
export function DemoDay({ onExit, onSignUp }: DemoDayProps) {
  const [recommendation, setRecommendation] = useState<WorkoutRecommendation | null>(null);
  const [recommendationSource, setRecommendationSource] = useState<'ai' | 'rules'>('ai');
  const [coachContext, setCoachContext] = useState<CoachContext | null>(null);
  const [coachIntro, setCoachIntro] = useState<string | null>(null);
  const [checkIn, setCheckIn] = useState<DailyCheckIn | null>(null);
  const [awaitingCheckIn, setAwaitingCheckIn] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [todayRating, setTodayRating] = useState<'up' | 'down' | null>(null);
  const [todayCompleted, setTodayCompleted] = useState<boolean | null>(null);

  const generate = async (todayCheckIn: DailyCheckIn | null) => {
    const context = buildCoachContext(
      DEMO_USER,
      DEMO_RACES,
      DEMO_WORKOUTS,
      DEMO_SLEEP,
      DEMO_RECOVERY,
      'oura',
      todayCheckIn
    );
    setCoachContext(context);
    setGenerating(true);
    try {
      const coach = await askCoach(context, [], true);
      setRecommendation(coachResponseToRecommendation(coach));
      setCoachIntro(coach.reply);
      setRecommendationSource('ai');
    } catch (err) {
      console.warn('Demo: AI coach unavailable, using rules baseline:', err);
      setRecommendation(
        generateWorkoutRecommendation(DEMO_USER, DEMO_WORKOUTS, DEMO_SLEEP, DEMO_RECOVERY, todayCheckIn)
      );
      setCoachIntro(null);
      setRecommendationSource('rules');
    } finally {
      setGenerating(false);
    }
  };

  const handleCheckIn = (feeling: CheckInFeeling, note: string | null) => {
    if (generating) return;
    const entry: DailyCheckIn = {
      day: localDate(),
      feeling,
      note,
    };
    setCheckIn(entry);
    setAwaitingCheckIn(false);
    generate(entry);
  };

  const handleSkipCheckIn = () => {
    if (generating) return;
    setAwaitingCheckIn(false);
    generate(null);
  };

  const handleCoachUpdate = (coach: CoachResponse) => {
    setRecommendation(coachResponseToRecommendation(coach));
  };

  return (
    <Dashboard
      user={DEMO_USER}
      recommendation={recommendation}
      recommendationSource={recommendationSource}
      races={DEMO_RACES}
      coachContext={coachContext}
      coachIntro={coachIntro}
      checkIn={checkIn}
      awaitingCheckIn={awaitingCheckIn}
      onCheckIn={handleCheckIn}
      onSkipCheckIn={handleSkipCheckIn}
      todayRating={todayRating}
      todayCompleted={todayCompleted}
      onRate={setTodayRating}
      onMarkCompleted={setTodayCompleted}
      onCoachUpdate={handleCoachUpdate}
      onRegenerate={() => !generating && generate(checkIn)}
      regenerating={generating}
      onSettingsClick={() => {}}
      demo
      demoStats={DEMO_STATS}
      onExitDemo={onExit}
      onSignUp={onSignUp}
    />
  );
}
